import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import "../../Pages/AddFund/AddFund.css"; // Assuming similar styling can be reused
import apiCall from "./../../API/API";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import debounce from "lodash.debounce";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import { toastFailed } from "../../Common/Data";
import ConfirmModal from "../../Component/ConfirmModel";

const AdminFakeWithdrawal = () => {
  const [userId, setUserId] = useState("");
  const [existUserName, setExistUserName] = useState({});
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [amount, setAmount] = useState("");
  const [withdrawalDate, setWithdrawalDate] = useState("");
  const [withdrawalTime, setWithdrawalTime] = useState("");

  const [fakeWithdrawals, setFakeWithdrawals] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limitFilter, setLimitFilter] = useState(20);
  const [filterDisplay, setFilterDisplay] = useState("none");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalSum, setTotalSum] = useState(0); // For total withdrawal amount

  const [searchParams, setSearchParams] = useState({
    username: "",
    minAmountFilter: "",
    maxAmountFilter: "",
    startDate: "",
    endDate: "",
    status: "", // If withdrawal status is applicable
    search: "", // General search for name/username
    uid: "",
  });
  const [dateFilterQuickSelect, setDateFilterQuickSelect] = useState("default");

  const [showDeleteSingleModal, setShowDeleteSingleModal] = useState(false);
  const [selectedTxIdToDelete, setSelectedTxIdToDelete] = useState(null);
  const token = localStorage.getItem("authToken");

  // Debounced fetch for fake withdrawals
  const fetchFakeWithdrawals = useCallback(
    debounce(async (params) => {
      setTableLoading(true);
      try {
        const { data, error } = await apiCall(
          "GET",
          `/get-withdrawal-record?${new URLSearchParams(params).toString()}`, // Adjust API endpoint as needed
          {
            headers: {
              Authorization: token,
            },
          }
        );
        if (data && data.data) {
          setFakeWithdrawals(data.data);
          setTotalPages(data.totalPages || 1);
          setTotalRecords(data.totalRecords || 0);
          setTotalSum(data.Sum || 0); // Assuming the API returns a sum of withdrawals
        } else if (error) {
          toast.error(error.message || "Failed to fetch fake withdrawals.");
        }
      } catch (err) {
        toast.error("An error occurred while fetching fake withdrawals.");
        console.error("Fetch Fake Withdrawals Error:", err);
      } finally {
        setTableLoading(false);
      }
    }, 500),
    [token]
  );

  // Effect to trigger withdrawal fetch on page/filter changes
  useEffect(() => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      startDate: searchParams.startDate,
      endDate: searchParams.endDate,
      ...Object.fromEntries(
        Object.entries(searchParams).filter(
          ([key, value]) =>
            value !== "" && value !== 0 && key !== "startDate" && key !== "endDate"
        )
      ),
    };
    fetchFakeWithdrawals(queryParams);
  }, [currentPage, limitFilter, searchParams, fetchFakeWithdrawals]);

  // Debounced user existence check
  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (userId) {
      setTypingTimeout(
        setTimeout(() => {
          checkUserExistence();
        }, 500)
      );
    } else {
      setExistUserName({});
    }

    return () => clearTimeout(typingTimeout);
  }, [userId]);

  const checkUserExistence = async () => {
    setLoading(true);
    try {
      const body = { username: userId };
      const { data, error } = await apiCall("post", "/check_username", body); // Reuse existing user check API
      if (data) {
        setExistUserName({ name: data.name, color: "var(--colorSuccess)" });
      } else if (error) {
        setExistUserName({ name: "User not exists", color: "var(--redIcon)" });
      }
    } catch (err) {
      toast.error("An error occurred while checking user existence.");
      console.error("User Existence Check Error:", err);
      setExistUserName({ name: "Error checking user", color: "var(--redIcon)" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!userId || existUserName.color !== "var(--colorSuccess)") {
      toast.error("Please enter a valid existing username.");
      setLoading(false);
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount.");
      setLoading(false);
      return;
    }
    if (!withdrawalDate) {
      toast.error("Please select a date for 'Withdrawal On'.");
      setLoading(false);
      return;
    }
    if (!withdrawalTime) {
      toast.error("Please select a time for 'Withdrawal On'.");
      setLoading(false);
      return;
    }

    const withdrawalDateTime = new Date(`${withdrawalDate}T${withdrawalTime}`);

    const requestBody = {
      username: userId,
      amount: parseFloat(amount),
      withdrawal_on: withdrawalDateTime.toISOString(), // Use a relevant field name for your backend
    };

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: token,
        },
      };
      const { data, error } = await apiCall(
        "post",
        "/record-withdrawal", // Adjust this API endpoint to your fake withdrawal API
        requestBody,
        config
      );

      if (data) {
        toast.success(data.message || "Fake withdrawal successfully recorded!");
        handleClearForm();
        // Refresh the table after successful withdrawal
        const queryParams = {
          page: currentPage,
          limit: limitFilter,
          startDate: searchParams.startDate,
          endDate: searchParams.endDate,
          ...Object.fromEntries(
            Object.entries(searchParams).filter(
              ([key, value]) =>
                value !== "" && value !== 0 && key !== "startDate" && key !== "endDate"
            )
          ),
        };
        fetchFakeWithdrawals(queryParams);
      } else if (error) {
        toast.error(error.message || "Failed to record fake withdrawal.");
      }
    } catch (err) {
      toast.error("An error occurred during fake withdrawal recording.");
      console.error("Admin Fake Withdrawal Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setUserId("");
    setExistUserName({});
    setAmount("");
    setWithdrawalDate("");
    setWithdrawalTime("");
  };

  const toggleFilter = () => {
    setFilterDisplay(filterDisplay === "none" ? "flex" : "none");
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (e) => {
    setLimitFilter(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleDateFilterQuickSelectChange = (e) => {
    const value = e.target.value;
    setDateFilterQuickSelect(value);
    const today = moment();
    let start = "";
    let end = "";

    switch (value) {
      case "today":
        start = today.clone().startOf("day").format("YYYY-MM-DD");
        end = today.format("YYYY-MM-DD");
        break;
      case "yesterday":
        start = today.clone().subtract(1, "days").startOf("day").format("YYYY-MM-DD");
        end = today.clone().subtract(1, "days").endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        start = today.clone().startOf("week").format("YYYY-MM-DD");
        end = today.clone().endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        start = today.clone().startOf("month").format("YYYY-MM-DD");
        end = today.clone().endOf("month").format("YYYY-MM-DD");
        break;
      default:
        start = "";
        end = "";
        break;
    }
    setSearchParams((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchParams({
      username: "",
      minAmountFilter: "",
      maxAmountFilter: "",
      startDate: "",
      endDate: "",
      status: "",
      search: "",
      uid: "",
    });
    setLimitFilter(20);
    setDateFilterQuickSelect("default");
    setCurrentPage(1);
  };

  
  const handleDeleteAllFakeWithdrawal = async () => {
    try {
      const { data, error } = await apiCall("post", "/delete-withdrawal");

      if (data && data.success) {
        fetchFakeWithdrawals();
        toast.success(data.message);
      } else {
        toastFailed("Failed to delete: " + (data?.message || error || "Unknown error"));
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err.message || "Failed to delete...");
    } finally{
      setShowModal(false);
    }
  };

  
  const handleDeleteSingleFakeWithdraw = async () => {
    // if (!selectedOrderIdToDelete) return;
    console.log("Tx_Id:", selectedTxIdToDelete)
    try {
      const { data, error } = await apiCall("post", "/delete-single-fake-withdrawal", {tx_Id: selectedTxIdToDelete});

      if (data && data.success) {
        fetchFakeWithdrawals();
        toast.success(data.message);
      } else {
        toastFailed("Failed to delete: " + (data?.message || error || "Unknown error"));
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err.message || "Failed to delete...");
    } finally {
      setShowDeleteSingleModal(false);
      setSelectedTxIdToDelete(null);
    }
  };
  

  return (
    <React.Fragment>
      <Container className="mt-4">
        <section id="admin-fake-withdrawal-section">
          <div className="d-flex justify-content-center">
            <h2>Record Fake Withdrawal</h2>
          </div>
          <section id="formContent">
            <div className="form">
              <Row>
                <Col lg={6} style={{ margin: "auto" }}>
                  <form onSubmit={handleSubmit}>
                    <div id="formFields">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="userId">User Username:</label>
                          <label style={{ textAlign: "end"}}>
                            {loading && userId ? "Checking..." : existUserName?.name}
                          </label>
                        </div>
                        <input
                          type="text"
                          id="userId"
                          className="form-control"
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          placeholder="Enter user's username"
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="amount">Amount:</label>
                        <input
                          type="number"
                          id="amount"
                          className="form-control"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Enter withdrawal amount"
                          step="any"
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="withdrawalDate">Date of Withdrawal On:</label>
                        <input
                          type="date"
                          id="withdrawalDate"
                          className="form-control"
                          value={withdrawalDate}
                          onChange={(e) => setWithdrawalDate(e.target.value)}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="withdrawalTime">Time of Withdrawal On:</label>
                        <input
                          type="time"
                          id="withdrawalTime"
                          className="form-control"
                          value={withdrawalTime}
                          onChange={(e) => setWithdrawalTime(e.target.value)}
                          required
                        />
                      </div>

                      <div className="d-flex justify-content-between mt-4">
                        <Button
                          variant="secondary"
                          className="me-2"
                          onClick={handleClearForm}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={loading || existUserName.color !== "var(--colorSuccess)"}
                        >
                          {loading ? "Recording Withdrawal..." : "Record Withdrawal"}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Col>
              </Row>
            </div>
          </section>
        </section>

        <section className="mt-5">
          <div className="d-flex justify-content-center mb-3">
            <h2>Fake Withdrawals History</h2>
          </div>

          <Row id="filterRow">
            <span id="filterLogo">
              <div className="d-flex justify-content-between">
                <div onClick={toggleFilter}>
                  <i><BiFilter /></i> Filter
                </div>
              </div>
               <div className="align-content-center">
                <button className="btn btn-danger" onClick={() => setShowModal(true)}>
                  Delete All Fake Withdrawal
                </button>

                <ConfirmModal
                  show={showModal}
                  handleClose={() => setShowModal(false)}
                  handleConfirm={handleDeleteAllFakeWithdrawal}
                  title="Delete All Fake Orders?"
                  body="Are you sure you want to delete all fake withdrawals? This action is permanent and cannot be undone."
                />
              </div>
            </span>

            <Row className="p-0 m-0" style={{ display: filterDisplay }}>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="text"
                    name="search"
                    placeholder="Search By Name, Username"
                    value={searchParams.search}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="text"
                    placeholder="Search User By Uid"
                    name="uid"
                    value={searchParams.uid}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="number"
                    name="minAmountFilter"
                    placeholder="Min Amount"
                    value={searchParams.minAmountFilter}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="number"
                    name="maxAmountFilter"
                    placeholder="Max Amount"
                    value={searchParams.maxAmountFilter}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              {/* If you have a status for withdrawals (e.g., pending, completed), uncomment this */}
              {/* <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    name="status"
                    value={searchParams.status}
                    onChange={handleSearchChange}
                    style={{ color: 'white' }}
                  >
                    <option value="">All Status</option>
                    <option value="1">Approved</option>
                    <option value="0">Pending</option>
                    <option value="2">Rejected</option>
                  </select>
                </div>
              </Col> */}
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="date"
                    name="startDate"
                    placeholder="Start Date"
                    value={searchParams.startDate}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="date"
                    name="endDate"
                    placeholder="End Date"
                    value={searchParams.endDate}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    name="dateFilterQuickSelect"
                    value={dateFilterQuickSelect}
                    onChange={handleDateFilterQuickSelectChange}
                    style={{ color: 'white' }}
                  >
                    <option value="default">Quick Date Filter</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                  </select>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    value={limitFilter}
                    onChange={handleLimitChange}
                    style={{ color: 'white' }}
                  >
                    <option value="20">Show 20</option>
                    <option value="50">Show 50</option>
                    <option value="100">Show 100</option>
                    <option value="200">Show 200</option>
                  </select>
                </div>
              </Col>
              <div>
                <button className="save filter" onClick={applyFilters}>
                  Filter{" "}
                  <i>
                    <BiFilter />
                  </i>
                </button>
                <button className="cancel reset" onClick={resetFilters}>
                  Reset{" "}
                  <i>
                    <GrPowerReset />
                  </i>
                </button>
              </div>
            </Row>
          </Row>

          <div id="directIncomeDiv">
            <Row id="directTableRow" className="text-center">
              <Col md="12" className="p-0">
                <div style={{ width: "100%", overflowX: "auto" }}>
                  <table>
                      <tr>
                        <th>S No.</th>
                        <th>Name</th>
                        <th>Username</th>
                        <th>UID</th>
                        <th>Amount</th>
                        {/* <th>Status</th> */}
                        {/* <th>Withdrawal On</th> */}
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    <tbody>
                      {tableLoading ? (
                        <tr>
                          <td colSpan="7">Loading fake withdrawals...</td>
                        </tr>
                      ) : fakeWithdrawals.length > 0 ? (
                        fakeWithdrawals.map((withdrawal, i) => (
                          <tr key={withdrawal._id}>
                            <td>{i + 1 + (currentPage - 1) * limitFilter}</td>
                            <td>{withdrawal.name}</td>
                            <td>{withdrawal.username || 'N/A'}</td>
                            <td>{withdrawal.uid}</td>
                            <td>{withdrawal.amount}</td>
                            {/* <td>{withdrawal.status === 1 ? "Approved" : withdrawal.status === 2 ? "Completed" : "Pending"}</td> */}
                            {/* <td>{moment(withdrawal.time).format("LLL")}</td> */}
                            <td>{moment(withdrawal.createdAt).format("LLL")}</td>
                            <td>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  setSelectedTxIdToDelete(withdrawal?.tx_Id);
                                  setShowDeleteSingleModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7">No fake withdrawals found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <ConfirmModal
                    show={showDeleteSingleModal}
                    handleClose={() => setShowDeleteSingleModal(false)}
                    handleConfirm={handleDeleteSingleFakeWithdraw}
                    title="Delete This Withdraw?"
                    body="Are you sure you want to delete this fake withdraw? This action is permanent and cannot be undone."
                  />
                </div>
              </Col>
            </Row>
          </div>

          <Row className="mt-3">
            <Col md="12">
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePagination}
              />
            </Col>
          </Row>
        </section>

        <ToastContainer className="toast-container" />
      </Container>
    </React.Fragment>
  );
};

export default AdminFakeWithdrawal;